<template>
    <div>
        <!-- Page Header -->
        <section class="bg-dark pt-5 pb-4">
            <div class="container py-5">
                <h1 class="text-light pt-1 pt-md-3 mb-4">
                    Sage Accounting Integration
                </h1>
                <p class="text-light opacity-75 mb-0">
                    Connect your Sage Accounting account to import clients for quick referrals
                </p>
            </div>
        </section>
        <!-- / Page Header -->

        <!-- Integration Content -->
        <section class="position-relative bg-white rounded-xxl-4 zindex-5" style="margin-top: -30px;">
            <div class="container pt-4 pb-5 mb-md-4">
                <!-- Breadcrumb-->
                <breadcrumb :crumbs="crumbs" class="pb-4 my-2"></breadcrumb>

                <!-- Is Loading -->
                <div v-if="isLoading" class="text-center">
                    <spinner></spinner>
                </div>
                <!-- / Is Loading -->

                <!-- Integration Status -->
                <div v-if="!isLoading" class="row">
                    <div class="col-lg-8">
                        <!-- Integration Card -->
                        <div class="card border-0 shadow-sm">
                            <div class="card-body p-4">
                                <div class="d-flex align-items-center mb-4">
                                    <div class="flex-shrink-0 me-3">
                                        <div class="rounded-3" style="width: 48px; height: 48px; overflow: hidden;">
                                            <img src="/img/sage.png" alt="Sage" style="width: 100%; height: 100%; object-fit: cover;">
                                        </div>
                                    </div>
                                    <div class="flex-grow-1">
                                        <h4 class="mb-1">Sage Accounting</h4>
                                        <p class="text-muted mb-0">Import clients for quick referrals</p>
                                    </div>
                                    <div class="flex-shrink-0">
                                        <span v-if="integrationStatus === 'connected'" 
                                              class="badge bg-success bg-opacity-10 text-success">
                                            <i class="fi-check-circle me-1"></i>
                                            Connected
                                        </span>
                                        <span v-else-if="integrationStatus === 'disconnected'" 
                                              class="badge bg-secondary bg-opacity-10 text-secondary">
                                            <i class="fi-alert-circle me-1"></i>
                                            Disconnected
                                        </span>
                                        <span v-else 
                                              class="badge bg-secondary bg-opacity-10 text-secondary">
                                            <i class="fi-clock me-1"></i>
                                            Checking...
                                        </span>
                                    </div>
                                </div>

                                <!-- Connection Status -->
                                <div v-if="integrationStatus === 'connected'" class="mb-4">
                                    <div class="alert alert-success">
                                        <h6 class="alert-heading">
                                            <i class="fi-check-circle me-2"></i>
                                            Successfully Connected
                                        </h6>
                                        <p class="mb-2">Your Sage Accounting account is connected and ready to import clients for referrals.</p>
                                        <div class="row">
                                            <!-- <div class="col-md-6">
                                                <small class="text-muted">
                                                    <strong>Business ID:</strong> {{ (integrationDetails && integrationDetails.business_id) || 'Unknown' }}
                                                </small>
                                            </div> -->
                                            <div class="col-md-6">
                                                <small class="text-muted">
                                                    <strong>Scope:</strong> {{ (integrationDetails && integrationDetails.scope) || 'Full Access' }}
                                                </small>
                                            </div>
                                            <div class="col-md-6">
                                                <small class="text-muted">
                                                    <strong>Token Expires:</strong> {{ formatTokenExpiry() }}
                                                </small>
                                            </div>
                                        </div>
                                        <!-- <div class="row mt-2"> -->
                                            <!-- <div class="col-md-6">
                                                <small class="text-muted">
                                                    <strong>Last Sync:</strong> {{ formatLastSync() }}
                                                </small>
                                            </div> -->
                                            <!-- <div class="col-md-6">
                                                <small class="text-muted">
                                                    <strong>Token Expires:</strong> {{ formatTokenExpiry() }}
                                                </small>
                                            </div> -->
                                        <!-- </div> -->
                                    </div>
                                </div>

                                <!-- Disconnected Status -->
                                <div v-else-if="integrationStatus === 'disconnected'" class="mb-4">
                                    <div class="alert alert-secondary">
                                        <h6 class="alert-heading">
                                            <i class="fi-alert-circle me-2"></i>
                                            Not Connected
                                        </h6>
                                        <p class="mb-0">Connect your Sage Accounting account to import clients for quick referrals and client management.</p>
                                    </div>
                                </div>

                                <!-- Error Status -->
                                <div v-else-if="integrationStatus === 'error'" class="mb-4">
                                    <div class="alert alert-danger">
                                        <h6 class="alert-heading">
                                            <i class="fi-x-circle me-2"></i>
                                            Connection Error
                                        </h6>
                                        <p class="mb-0">There was an error checking your integration status. Please try again.</p>
                                    </div>
                                </div>

                                <!-- Integration Actions -->
                                <div class="d-flex gap-2 flex-wrap">
                                    <button v-if="integrationStatus === 'disconnected'" 
                                            @click="connectSage" 
                                            class="btn btn-primary"
                                            :disabled="isConnecting">
                                        <spinner v-if="isConnecting" class="me-2"></spinner>
                                        <i v-else class="fi-link me-2"></i>
                                        {{ isConnecting ? 'Connecting...' : 'Connect Sage Account' }}
                                    </button>
                                    
                                    <button v-if="integrationStatus === 'connected'" 
                                            @click="openContactsModal" 
                                            class="btn btn-primary"
                                            :disabled="isLoadingContacts">
                                        <spinner v-if="isLoadingContacts" class="me-2"></spinner>
                                        <i v-else class="fi-users me-2"></i>
                                        {{ isLoadingContacts ? 'Loading...' : 'View Contacts' }}
                                    </button>
                                    
                                    <button v-if="integrationStatus === 'connected'" 
                                            @click="disconnectSage" 
                                            class="btn btn-outline-danger"
                                            :disabled="isDisconnecting">
                                        <spinner v-if="isDisconnecting" class="me-2"></spinner>
                                        {{ isDisconnecting ? 'Disconnecting...' : 'Disconnect Sage' }}
                                    </button>
                                    
                                    <button v-if="integrationStatus === 'connected'" 
                                            @click="refreshData" 
                                            class="btn btn-outline-primary"
                                            :disabled="isRefreshing">
                                        <spinner v-if="isRefreshing" class="me-2"></spinner>
                                        <i v-else class="fi-refresh me-2"></i>
                                        {{ isRefreshing ? 'Refreshing...' : 'Refresh' }}
                                    </button>
                                    
                                    <button v-if="integrationStatus === 'error'" 
                                            @click="checkIntegrationStatus" 
                                            class="btn btn-outline-secondary">
                                        <i class="fi-refresh me-2"></i>
                                        Retry
                                    </button>
                                </div>
                            </div>
                        </div>
                        <!-- / Integration Card -->

                    </div>

                    <!-- Sidebar -->
                    <div class="col-lg-4">
                        <div class="card border-0 shadow-sm">
                            <div class="card-body p-4">
                                <h5 class="mb-3">Need Help?</h5>
                                <p class="text-muted mb-3">
                                    Having trouble connecting your Sage account?
                                </p>
                                <a href="https://syndesi.network/contact/" 
                                   target="_blank" 
                                   class="btn btn-outline-primary">
                                    <i class="fi-help me-2"></i>
                                    Contact Support
                                </a>
                            </div>
                        </div>
                    </div>
                    <!-- / Sidebar -->
                </div>
                <!-- / Integration Status -->

            </div>
        </section>
        <!-- / Integration Content -->

        <!-- Contacts Modal -->
        <div v-if="showContactsModal" class="modal fade show" style="display: block;" tabindex="-1">
            <div class="modal-dialog modal-xl">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">
                            <i class="fi-users me-2"></i>
                            Sage Contacts
                        </h5>
                        <button type="button" class="btn-close" @click="closeContactsModal"></button>
                    </div>
                    <div class="modal-body">
                        <!-- Search Form -->
                        <div class="row mb-4">
                            <div class="col-md-4">
                                <label for="contactSearch" class="form-label">Search Contacts</label>
                                <input 
                                    type="text" 
                                    id="contactSearch"
                                    class="form-control" 
                                    v-model="contactSearch" 
                                    placeholder="Search by name..."
                                    @keyup.enter="searchContacts">
                            </div>
                            <div class="col-md-3">
                                <label for="contactType" class="form-label">Contact Type</label>
                                <select 
                                    id="contactType"
                                    class="form-select" 
                                    v-model="contactTypeFilter">
                                    <option value="">All Types</option>
                                    <option value="CUSTOMER">Customer</option>
                                    <option value="VENDOR">Vendor</option>
                                </select>
                            </div>
                            <div class="col-md-3">
                                <label for="itemsPerPage" class="form-label">Items per Page</label>
                                <select 
                                    id="itemsPerPage"
                                    class="form-select" 
                                    v-model="itemsPerPage">
                                    <option value="10">10</option>
                                    <option value="20">20</option>
                                    <option value="50">50</option>
                                </select>
                            </div>
                            <div class="col-md-2 d-flex align-items-end">
                                <button 
                                    class="btn btn-primary w-100" 
                                    @click="searchContacts"
                                    :disabled="isLoadingContacts">
                                    <spinner v-if="isLoadingContacts" class="spinner-border-sm me-2"></spinner>
                                    <i v-else class="fi-search me-2"></i>
                                    Search
                                </button>
                            </div>
                        </div>

                        <!-- Loading State -->
                        <div v-if="isLoadingContacts && !contacts.length" class="text-center py-5">
                            <spinner></spinner>
                            <p class="mt-2 text-muted">Loading contacts...</p>
                        </div>

                        <!-- No Results -->
                        <div v-else-if="!isLoadingContacts && !contacts.length && hasSearched" class="text-center py-5">
                            <i class="fi-users text-muted" style="font-size: 3rem;"></i>
                            <p class="mt-2 text-muted">No contacts found matching your criteria.</p>
                        </div>

                        <!-- Contacts Table -->
                        <div v-else-if="contacts.length" class="table-responsive">
                            <table class="table table-hover">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Email</th>
                                        <th>Phone</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="contact in contacts" :key="contact.id">
                                        <td>
                                            <div class="d-flex align-items-center">
                                                <div class="avatar avatar-xs bg-primary bg-opacity-10 text-primary rounded-circle me-2">
                                                    {{ getInitials(contact.displayed_as) }}
                                                </div>
                                                <div>
                                                    <div class="fw-medium">{{ contact.displayed_as }}</div>
                                                    <small class="text-muted">ID: {{ contact.id }}</small>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <span v-if="contact.email">{{ contact.email }}</span>
                                            <span v-else class="text-muted">—</span>
                                        </td>
                                        <td>
                                            <span v-if="contact.telephone">{{ contact.telephone }}</span>
                                            <span v-else class="text-muted">—</span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            <!-- Pagination -->
                            <div v-if="contactsPagination" class="d-flex justify-content-between align-items-center mt-4">
                                <div class="text-muted">
                                    Showing {{ ((contactsPagination.$page - 1) * contactsPagination.$itemsPerPage) + 1 }} 
                                    to {{ Math.min(contactsPagination.$page * contactsPagination.$itemsPerPage, contactsPagination.$total) }} 
                                    of {{ contactsPagination.$total }} contacts
                                </div>
                                <nav>
                                    <ul class="pagination mb-0">
                                        <li class="page-item" :class="{ disabled: contactsPagination.$page <= 1 }">
                                            <button class="page-link" @click="goToPage(contactsPagination.$page - 1)" :disabled="contactsPagination.$page <= 1">
                                                <i class="fi-chevron-left"></i>
                                            </button>
                                        </li>
                                        <li v-for="page in visiblePages" :key="page" class="page-item" :class="{ active: page === contactsPagination.$page }">
                                            <button class="page-link" @click="goToPage(page)">{{ page }}</button>
                                        </li>
                                        <li class="page-item" :class="{ disabled: !contactsPagination.$next }">
                                            <button class="page-link" @click="goToPage(contactsPagination.$page + 1)" :disabled="!contactsPagination.$next">
                                                <i class="fi-chevron-right"></i>
                                            </button>
                                        </li>
                                    </ul>
                                </nav>
                            </div>
                        </div>

                        <!-- Initial State -->
                        <div v-else class="text-center py-5">
                            <i class="fi-search text-muted" style="font-size: 3rem;"></i>
                            <p class="mt-2 text-muted">Enter search criteria and click "Search" to find contacts.</p>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" @click="closeContactsModal">Close</button>
                    </div>
                </div>
            </div>
        </div>
        <!-- / Contacts Modal -->

        <!-- Modal Backdrop -->
        <div v-if="showContactsModal" class="modal-backdrop fade show"></div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Spinner from '../../components/Spinner.vue';
import Breadcrumb from '../../components/Breadcrumb.vue';
import axios from 'axios';
import { useToast } from 'vue-toastification';

const toast = useToast();

export default {
    name: 'SageIntegration',
    
    components: { 
        Spinner, 
        Breadcrumb 
    },

    data() {
        return {
            isLoading: false,
            isConnecting: false,
            isDisconnecting: false,
            isRefreshing: false,
            integrationStatus: 'checking', // checking, connected, disconnected, error
            integrationDetails: null,
            
            // Contacts Modal
            showContactsModal: false,
            isLoadingContacts: false,
            contacts: [],
            contactsPagination: null,
            hasSearched: false,
            
            // Search Filters
            contactSearch: '',
            contactTypeFilter: 'CUSTOMER',
            itemsPerPage: 20,
            currentPage: 1
        }
    },

    computed: {
        ...mapGetters('auth', {
            user: 'user',
            userRole: 'userRole',
            isAdvisor: 'isAdvisor'
        }),

        crumbs() {
            return [
                {
                    href: "/",
                    label: "Dashboard",
                },
                {
                    href: "/integrations",
                    label: "Integrations",
                },
                {
                    href: "#",
                    label: "Sage Accounting",
                    active: true,
                },
            ];
        },

        visiblePages() {
            if (!this.contactsPagination) return [];
            
            const totalPages = Math.ceil(this.contactsPagination.$total / this.contactsPagination.$itemsPerPage);
            const currentPage = this.contactsPagination.$page;
            const pages = [];
            
            // Show up to 5 pages around current page
            const startPage = Math.max(1, currentPage - 2);
            const endPage = Math.min(totalPages, currentPage + 2);
            
            for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
            }
            
            return pages;
        }
    },

    created() {
        // Check if user is an advisor, if not redirect
        if (!this.isAdvisor) {
            this.$router.push('/');
            return;
        }

        this.checkIntegrationStatus();
    },

    methods: {
        async checkIntegrationStatus() {
            this.isLoading = true;
            this.integrationStatus = 'checking';

            try {
                const response = await axios.get(`${window.API_BASE}/sage/check-integration`);

                if (response.data.has_integration) {
                    this.integrationStatus = 'connected';
                    this.integrationDetails = response.data.integration_details;
                } else {
                    this.integrationStatus = 'disconnected';
                    this.integrationDetails = null;
                }
            } catch (error) {
                console.error('Failed to check integration status:', error);
                this.integrationStatus = 'error';
                this.integrationDetails = null;
            } finally {
                this.isLoading = false;
            }
        },

        async connectSage() {
            this.isConnecting = true;

            try {
                const response = await axios.post(`${window.API_BASE}/sage/authorization-url`);

                // Redirect to Sage authorization
                window.location.href = response.data.authorization_url;
            } catch (error) {
                console.error('Failed to initiate Sage connection:', error);
                toast.error('Failed to start Sage connection. Please try again.');
            } finally {
                this.isConnecting = false;
            }
        },

        disconnectSage() {
            swal({
                title: "Are you sure you want to disconnect Sage?",
                text: "This will remove the connection to your Sage Accounting account and you'll need to reconnect to import clients.",
                icon: "warning",
                dangerMode: true,
                buttons: {
                    cancel: true,
                    confirm: "Yes, Disconnect"
                }
            }).then((shouldDisconnect) => {
                if (shouldDisconnect) {
                    this.performDisconnect();
                }
            });
        },

        async performDisconnect() {
            this.isDisconnecting = true;

            try {
                const response = await axios.post(`${window.API_BASE}/sage/deactivate`);

                if (response.data.success) {
                    this.integrationStatus = 'disconnected';
                    this.integrationDetails = null;
                    toast.success('Sage integration disconnected successfully');
                } else {
                    toast.warning('No active integration to disconnect');
                }
            } catch (error) {
                console.error('Failed to disconnect Sage:', error);
                toast.error('Failed to disconnect Sage integration');
            } finally {
                this.isDisconnecting = false;
            }
        },

        async refreshData() {
            this.isRefreshing = true;

            try {
                // This would be a call to refresh/update the integration data
                // For now, we'll just recheck the status
                await this.checkIntegrationStatus();
                toast.success('Integration data refreshed successfully');
            } catch (error) {
                console.error('Failed to refresh data:', error);
                toast.error('Failed to refresh integration data');
            } finally {
                this.isRefreshing = false;
            }
        },

        formatLastSync() {
            if (!this.integrationDetails || !this.integrationDetails.last_sync_at) {
                return 'Never';
            }
            return new Date(this.integrationDetails.last_sync_at).toLocaleDateString();
        },

        formatTokenExpiry() {
            if (!this.integrationDetails || !this.integrationDetails.token_expires_at) {
                return 'Unknown';
            }
            return new Date(this.integrationDetails.token_expires_at).toLocaleDateString();
        },

        // Contacts Modal Methods
        openContactsModal() {
            this.showContactsModal = true;
            this.resetContactsSearch();
        },

        closeContactsModal() {
            this.showContactsModal = false;
            this.resetContactsSearch();
        },

        resetContactsSearch() {
            this.contacts = [];
            this.contactsPagination = null;
            this.hasSearched = false;
            this.contactSearch = '';
            this.contactTypeFilter = 'CUSTOMER';
            this.itemsPerPage = 20;
            this.currentPage = 1;
        },

        async searchContacts(page = 1) {
            this.isLoadingContacts = true;
            this.currentPage = page;

            try {
                const params = {
                    items_per_page: parseInt(this.itemsPerPage),
                    page: parseInt(page) || 1
                };

                // Add search term if provided
                if (this.contactSearch.trim()) {
                    params.search = this.contactSearch.trim();
                }

                // Add contact type filter if selected
                if (this.contactTypeFilter) {
                    params.contact_type_id = this.contactTypeFilter;
                }

                const response = await axios.get(`${window.API_BASE}/sage/contacts`, {
                    params: params
                });

                this.contacts = response.data.$items || [];
                this.contactsPagination = {
                    $total: response.data.$total || 0,
                    $page: response.data.$page || 1,
                    $next: response.data.$next || null,
                    $itemsPerPage: response.data.$itemsPerPage || parseInt(this.itemsPerPage)
                };
                this.hasSearched = true;

            } catch (error) {
                console.error('Failed to fetch contacts:', error);
                toast.error('Failed to fetch contacts. Please try again.');
                this.contacts = [];
                this.contactsPagination = null;
                this.hasSearched = true;
            } finally {
                this.isLoadingContacts = false;
            }
        },

        goToPage(page) {
            if (page < 1 || !this.contactsPagination) return;
            
            const totalPages = Math.ceil(this.contactsPagination.$total / this.contactsPagination.$itemsPerPage);
            if (page > totalPages) return;

            this.searchContacts(page);
        },

        getInitials(name) {
            if (!name) return '?';
            return name.split(' ')
                .map(word => word.charAt(0))
                .join('')
                .toUpperCase()
                .substring(0, 2);
        }
    }
}
</script>

<style scoped>
.card {
    transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}

.card:hover {
    transform: translateY(-2px);
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
}
</style> 